import { lazy, Suspense } from "react";
import { defineLinearQuiz } from "@/schema/defineLinearQuiz";
import { assetUrl } from "./aural/assets";
import type { Severity } from "./aural/profiles";

const AuralWaterReport = lazy(() => import("./aural/report"));

const PDP = "https://myaural.com/";

export const auralPielPeloQuiz = defineLinearQuiz({
  id: "aural-piel-pelo",
  slug: "piel-pelo",
  tenant: "aural",
  meta: {
    locale: "es",
    title: "Test de piel y cabello — AURAL",
    introHeadline: "¿El agua de tu ducha está dañando tu piel y tu pelo?",
    introBody: "Respondé unas preguntas y descubrí qué le está haciendo el agua de tu ducha. Toma 2 minutos.",
    testimonial: {
      quote:
        "Si no hubiese hecho el quiz en 2 minutos, nunca me hubiese enterado que mi agua estaba dañando mi pelo y mi piel.",
      author: "Caro M., clienta AURAL",
    },
  },
  questions: [
    {
      id: "q_genero",
      type: "single",
      // Pure lead data: intentionally absent from `scoring.points` (contributes 0,
      // thresholds unchanged). Doubles as the landing — the hero renders above it.
      prompt: "Para empezar, ¿con qué te identificás?",
      whyWeAsk: "Para personalizar tu informe. Nunca compartimos tus datos.",
      options: [
        { value: "mujer", label: "Mujer" },
        { value: "hombre", label: "Hombre" },
        { value: "prefiero_no_decir", label: "Prefiero no decirlo" },
      ],
    },
    {
      id: "q_piel",
      type: "single",
      prompt: "Después de ducharte, ¿cómo sentís tu piel?",
      options: [
        { value: "comoda", label: "Cómoda e hidratada" },
        { value: "algo_tirante", label: "Algo tirante" },
        { value: "seca", label: "Seca o tirante seguido" },
        { value: "muy_seca", label: "Muy seca, pica o se descama" },
      ],
    },
    {
      id: "q_pelo",
      type: "single",
      prompt: "¿Cómo describirías tu cabello últimamente?",
      options: [
        { value: "sano", label: "Sano y con brillo" },
        { value: "algo_opaco", label: "Algo opaco" },
        { value: "seco", label: "Seco o quebradizo" },
        { value: "muy_danado", label: "Muy dañado, sin vida" },
      ],
    },
    {
      id: "q_sintomas",
      type: "multi",
      prompt: "¿Cuáles de estos notás?",
      helper: "Elegí los que apliquen.",
      min: 0,
      options: [
        { value: "picazon", label: "Picazón en la piel" },
        { value: "caspa", label: "Caspa o cuero cabelludo seco" },
        { value: "frizz", label: "Frizz o encrespamiento" },
        { value: "color", label: "Se me va el color del pelo" },
        { value: "ninguno", label: "Ninguno de estos" },
      ],
    },
    {
      id: "info_edu",
      type: "info",
      heading: "El enemigo invisible",
      body: [
        "No lo ves, pero cada ducha deja algo en tu piel y tu pelo. El agua de red arrastra cuatro culpables que actúan todos los días, sin que lo notes.",
        "Estos son los que AURAL filtra antes de que toquen tu cuerpo:",
      ],
      cards: [
        {
          title: "El Cloro",
          description: "Reseca tu piel y apaga el brillo del pelo en cada ducha caliente.",
          image: assetUrl("villain-cloro.webp"),
          imageAlt: "El cloro, ilustrado como un villano de vapor",
        },
        {
          title: "La Cal",
          description: "Forma una capa mineral que tu crema no atraviesa y deja el pelo áspero.",
          image: assetUrl("villain-cal.webp"),
          imageAlt: "La cal, ilustrada como un villano de piedra",
        },
        {
          title: "Los Metales",
          description: "Se acumulan en el pelo, opacan el color y le quitan vida.",
          image: assetUrl("villain-metales.webp"),
          imageAlt: "Los metales, ilustrados como un villano metálico",
        },
        {
          title: "El Sedimento",
          description: "Óxido y partículas de cañerías viejas que irritan la piel sensible.",
          image: assetUrl("villain-sedimento.webp"),
          imageAlt: "El sedimento, ilustrado como un villano de óxido",
        },
      ],
    },
    {
      id: "q_productos",
      type: "single",
      prompt: "¿Invertís en cremas, mascarillas o tratamientos para tu piel o tu pelo?",
      options: [
        { value: "no", label: "Casi nada" },
        { value: "algo", label: "Algo" },
        { value: "bastante", label: "Bastante, pero sin los resultados que espero" },
      ],
    },
    {
      id: "q_color",
      type: "single",
      prompt: "¿Tenés el pelo teñido o con tratamiento de color?",
      options: [
        { value: "no", label: "No" },
        { value: "si", label: "Sí" },
      ],
    },
    {
      id: "q_cp",
      type: "text",
      prompt: "¿Cuál es tu código postal?",
      placeholder: "0000",
      whyWeAsk:
        "El cloro, la dureza y los metales del agua cambian por zona. Con tu CP armamos tu informe real, no uno genérico.",
    },
  ],

  personas: [
    {
      key: "alto",
      label: "El agua te está pasando factura",
      result: {
        tagline: "Tus respuestas apuntan a un daño claro por el agua de tu ducha.",
        body: [
          "El cloro y los metales del agua de red están resecando tu piel y castigando tu pelo a diario.",
          "La buena noticia: se frena desde la primera ducha con agua filtrada.",
          "AURAL filtra cloro, cal y metales en 20 etapas para devolverte piel suave y pelo con brillo.",
        ],
        cta: { label: "ELEVÁ TU DUCHA", href: PDP },
      },
    },
    {
      key: "moderado",
      label: "Señales tempranas",
      result: {
        tagline: "Hay indicios de que el agua ya te está afectando.",
        body: [
          "Todavía es leve, pero esos signos suelen ir a más si el agua sigue sin filtrar.",
          "Actuar ahora protege tu piel y tu pelo —y lo que invertís en cuidarlos.",
          "AURAL se instala en minutos y filtra el agua de cada ducha.",
        ],
        cta: { label: "ELEVÁ TU DUCHA", href: PDP },
      },
    },
    {
      key: "leve",
      label: "Prevención inteligente",
      result: {
        tagline: "Tu piel y tu pelo están bien. Mantengámoslo así.",
        body: [
          "Aunque no lo notes, el cloro del agua desgasta la hidratación y el brillo con el tiempo, y desvanece el color.",
          "Filtrar el agua protege ese buen estado y cuida tu inversión en productos.",
          "AURAL es el primer paso de una rutina que empieza en la ducha.",
        ],
        cta: { label: "ELEVÁ TU DUCHA", href: PDP },
      },
    },
  ],

  // Linear branching is derived from question order; threshold scoring is declarative.
  scoring: {
    // Per-question point maps for single-choice answers (higher = stronger water-damage signal).
    points: {
      q_piel: { comoda: 0, algo_tirante: 1, seca: 2, muy_seca: 3 },
      q_pelo: { sano: 0, algo_opaco: 1, seco: 2, muy_danado: 3 },
      q_productos: { no: 0, algo: 1, bastante: 2 },
      q_color: { no: 0, si: 1 },
    },
    multiSelect: { q_sintomas: { cap: 3, exclude: "ninguno" } },
    thresholds: [
      [7, "alto"],
      [3, "moderado"],
    ],
    fallback: "leve",
  },

  renderResult: ({ answers, persona, trackCta }) => (
    <Suspense fallback={<div style={{ minHeight: 240 }} aria-busy="true" />}>
      <AuralWaterReport answers={answers} severity={persona.key as Severity} trackCta={trackCta} />
    </Suspense>
  ),
});
